import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Share2, Heart, ChevronLeft, ChevronRight, Play, Maximize2 } from "lucide-react";
import { imageUrl } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

const IconBtn = ({ onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid} className="w-10 h-10 rounded-full bg-white/95 backdrop-blur shadow flex items-center justify-center hover:scale-105 transition-transform">{children}</button>
);

export const VideoPoster = ({ m, className }) => {
  const [bad, setBad] = useState(false);
  return m.poster && !bad
    ? <img src={imageUrl(m.poster)} alt="" draggable={false} onError={() => setBad(true)} className={className} />
    : <video src={`${imageUrl(m.src)}#t=0.1`} muted playsInline preload="metadata" className={`${className} pointer-events-none`} />;
};

// Media gallery: images + optional video slide (poster + play button). Tap any slide → full-screen view.
export function Gallery({ media, idx, setIdx, alt, isFav, onFav, onShare, onOpen }) {
  const images = media;
  const navigate = useNavigate();
  const { dir } = useLang();
  const touch = useRef(null);
  const n = images.length;
  const go = (d) => n > 1 && setIdx((i) => (i + d + n) % n);
  const Back = dir === "rtl" ? ArrowRight : ArrowLeft;
  const onEnd = (e) => {
    if (touch.current == null) return;
    const dx = e.changedTouches[0].clientX - touch.current;
    touch.current = null;
    if (Math.abs(dx) > 40) go((dx < 0 ? 1 : -1) * (dir === "rtl" ? -1 : 1));
  };
  return (
    <div className="relative bg-slate-100 aspect-[4/3] lg:aspect-[16/11] rounded-2xl sm:rounded-3xl overflow-hidden select-none" onTouchStart={(e) => { touch.current = e.touches[0].clientX; }} onTouchEnd={onEnd} data-testid="detail-gallery">
      <div className="flex h-full transition-transform duration-300" style={{ transform: `translateX(${(dir === "rtl" ? 1 : -1) * idx * 100}%)` }}>
        {media.map((m, i) => (
          <button key={i} type="button" onClick={() => onOpen(i)} className="relative w-full h-full shrink-0 cursor-pointer" data-testid={m.type === "video" ? "detail-video-slide" : `detail-image-slide-${i}`}>
            {m.type === "video" ? <>
              <VideoPoster m={m} className="w-full h-full object-cover bg-black" />
              <span className="absolute inset-0 flex items-center justify-center bg-black/25"><span className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center shadow-lg"><Play className="w-8 h-8 text-[#1A5C3A] fill-current ms-1" /></span></span>
            </> : <img src={imageUrl(m.src)} alt={alt} draggable={false} className="w-full h-full object-cover" data-testid={i === idx ? "detail-main-image" : undefined} />}
          </button>
        ))}
      </div>
      <div className="absolute top-4 inset-x-4 flex items-center justify-between">
        <IconBtn onClick={() => navigate(-1)} testid="back-button"><Back className="w-5 h-5" /></IconBtn>
        <div className="flex gap-2">
          <IconBtn onClick={onShare} testid="detail-share-icon"><Share2 className="w-5 h-5 text-slate-700" /></IconBtn>
          <IconBtn onClick={onFav} testid="detail-favorite-button"><Heart className={`w-5 h-5 ${isFav ? "fill-red-500 text-red-500" : "text-slate-700"}`} /></IconBtn>
        </div>
      </div>
      {n > 1 && (
        <>
          <button onClick={() => go(dir === "rtl" ? 1 : -1)} className="hidden sm:flex absolute top-1/2 -translate-y-1/2 left-3 w-9 h-9 rounded-full bg-white/90 shadow items-center justify-center" data-testid="detail-image-left"><ChevronLeft className="w-5 h-5" /></button>
          <button onClick={() => go(dir === "rtl" ? -1 : 1)} className="hidden sm:flex absolute top-1/2 -translate-y-1/2 right-3 w-9 h-9 rounded-full bg-white/90 shadow items-center justify-center" data-testid="detail-image-right"><ChevronRight className="w-5 h-5" /></button>
        </>
      )}
      {media[idx]?.type !== "video" && n > 0 && <span className="absolute bottom-4 end-4 w-9 h-9 rounded-full bg-black/50 text-white flex items-center justify-center pointer-events-none"><Maximize2 className="w-4 h-4" /></span>}
      <span className="absolute bottom-4 start-4 bg-black/60 text-white text-xs font-semibold px-2.5 py-1 rounded-full" dir="ltr" data-testid="detail-photo-counter">{n ? idx + 1 : 0}/{n}</span>
    </div>
  );
}
